import { AnalyticService } from '@application/analytic/analytic.service';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import type { DateService } from '@application/time/date.service';
import { DialogAlertService } from '@component/dialog_alert/dialog_alert.service';
import { RouterService } from '@cortex-shim/router.service';
import { CaseCommunicator } from '@feature/cases/_case_communicator';
import { MessagingCaseModel } from '@feature/cases/case_messaging_model';
import { FormManager } from '@feature/dynamic_form/form_manager/mod';
import { FileWorkerStale } from '@feature/file_manager/mod';
import { UnhandledIssuesService } from '@feature/issues/unhandled_issues.service';
import type { SocketController } from '@visiba/axon';
import { Injector } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { Presentation } from '@visiba/presentation';
import { v4 } from 'uuid';
import type { ChatCommunicatorProtocol, ParsedMessage } from './chat_communicator_protocol';
import type { ControlledChatMessage, ControlledMessage, ControlledSystemMessage } from './common';

interface PresentationForBridge {
	hasReceivedHistory: boolean;
	isCompleted: boolean;
	windowHasFocus: boolean;
	messages: ControlledMessage[];
}

interface PeerHasBeenReadUntilReadBy {
	clientId: number | null;
	commonName: string;
	givenName: string;
	surname: string;
	initials: string;
	participantId: number;
	resourceId: number;
}

interface PeerHasBeenReadUntilData {
	participantId: number;
	timestamp: string;
	readBy: PeerHasBeenReadUntilReadBy;
}

interface PeerHasBeenReadUntilPayload {
	caseId: number;
	data: PeerHasBeenReadUntilData;
}

export interface ChatCommunicatorMetadata {
	readonly hasReadUntilTimestamp: VisibaDate | null;
	readonly issuesTrackedById?: string;
	readonly onConnectionError?: (error: unknown) => void;
	readonly onUnauthorized?: () => void;
}

export class ChatCommunicator {
	public readonly presentation = Presentation.create<PresentationForBridge>({
		hasReceivedHistory: false,
		isCompleted: false,
		windowHasFocus: window.document.visibilityState === 'visible' && window.document.hasFocus(),
		messages: [],
	});
	public readonly formManager: FormManager;

	private disposers: (() => void)[] = [];
	private lastReadMessageTimestamp: VisibaDate | null;
	private readonly logger = new Logger('ChatCommunicator');

	constructor(
		private readonly communicatorProtocol: ChatCommunicatorProtocol,
		private readonly issuesTrackedById: string | null,
		private readonly connectionId: string,
		private readonly metaData: ChatCommunicatorMetadata,
		private readonly dateService: DateService,
		private readonly controller: SocketController,
		private readonly analyticService: AnalyticService,
		private readonly injector: Injector,
		private readonly unhandledIssuesService: UnhandledIssuesService,
		private readonly routerService: RouterService,
		private readonly dialogAlertService: DialogAlertService,
		private readonly caseCommunicator: CaseCommunicator,
	) {
		this.bindEvents();
		this.lastReadMessageTimestamp = this.metaData.hasReadUntilTimestamp;
		this.formManager = this.injector.resolve(FormManager);
	}

	public sendMessage(message: string | null, fileTickets: string[] = []): void {
		this.controller.publish(
			this.communicatorProtocol.topics.sendMessage,
			this.communicatorProtocol.parseOutgoingMessage(message, fileTickets),
		);

		const analyticQuirk_userOnlySentText = message != null && fileTickets.length === 0;
		const analyticQuirk_action = analyticQuirk_userOnlySentText ? 'Message with text sent' : 'Message with attachment sent';

		this.analyticService.trackEvent(this.communicatorProtocol.analyticCategory, analyticQuirk_action);

		// Todo: do local diff checks. So we don't need to wait for the messages
		// you've sent on the client.
		// this.updateMessages([
		// 	{
		// 		id: v4(),
		// 		isMe: true,
		// 		fromId: -1,
		// 		hasRendered: false,
		// 		text: message,
		// 	},
		// ]);
	}

	public getAllMessages(): void {
		this.communicatorProtocol.getHistory(this.controller, (response) => {
			if (!response || !this.communicatorProtocol.isValidSession(response)) {
				this.logger.logError('Invalid session or null response when getting messages');
				this.metaData.onConnectionError?.('Invalid session or case not found');
				return;
			}

			const messages = this.communicatorProtocol.parseIncomingMessages(response);

			this.handleRetrieveMessage(messages, true);

			this.presentation.write({
				hasReceivedHistory: true,
			});
		});
	}

	public destroy(): void {
		this.logger.logInformation('Destroyed bind events for chat');
		this.disposers.forEach((disposer) => disposer());
	}

	public setReadUntil(controlledMessage: ControlledChatMessage | ControlledSystemMessage): void {
		if (controlledMessage.hasBeenRead) return;
		// To return to more prioritized work.
		queueMicrotask(() => {
			controlledMessage.hasBeenRead = true;
			const readTimestamp = this.dateService.newDate();
			const timestamp = readTimestamp.add(250, 'milliseconds');
			if (timestamp.isAfter(this.lastReadMessageTimestamp)) {
				this.lastReadMessageTimestamp = timestamp;

				this.controller.publish('setReadUntil', {
					timestamp: timestamp.toISOString(),
					caseId: this.connectionId,
				});
			}

			if (this.issuesTrackedById != null) {
				this.unhandledIssuesService.removeIssuesByTag(this.issuesTrackedById, 'unreadMessages');
			}
		});
	}

	private getMessageWithReceiptIndex(messages: ControlledMessage[], payloadData: PeerHasBeenReadUntilData) {
		if (payloadData != null) {
			return messages.findLastIndex((message) => {
				return message.type === 'chatMessage' && message.isMe && message.timestamp.isBefore(payloadData.timestamp);
			});
		}

		return -1;
	}

	private bindEvents(): void {
		this.logger.logInformation('Bind events for chat');

		this.controller.onOpen = () => {
			this.logger.logInformation('Chat controller invoked "onOpen"');

			queueMicrotask(() => {
				this.getAllMessages();
			});
		};

		const setWindowVisibilityCallback = (): void => {
			this.presentation.write({ windowHasFocus: window.document.visibilityState === 'visible' && window.document.hasFocus() });
		};

		// Since visibilitychange maintain visibilityState as "visible" even if the browser
		// window is below other windows we need to maintain this state using the focus/blur-events
		// as well. This will ensure that we will only send read-receipts when the user has focused
		// the browser content and the window/tab is visible.
		window.addEventListener('visibilitychange', setWindowVisibilityCallback, false);
		window.addEventListener('focus', setWindowVisibilityCallback, false);
		window.addEventListener('blur', setWindowVisibilityCallback, false);

		this.controller.onError = (error) => {
			this.logger.logError('Chat controller error', error);
			this.metaData.onConnectionError?.(error);
		};

		this.disposers = [
			() => {
				window.removeEventListener('visibilitychange', setWindowVisibilityCallback, false);
				window.removeEventListener('focus', setWindowVisibilityCallback, false);
				window.removeEventListener('blur', setWindowVisibilityCallback, false);
			},

			this.controller.on(this.communicatorProtocol.topics.retrieveMessages, (response: unknown) => {
				if (!response || !this.communicatorProtocol.isValidSession(response)) {
					this.logger.logError('Invalid session or null response when retrieving messages');
					this.metaData.onConnectionError?.('Invalid session or case not found');
					return;
				}

				const messages = this.communicatorProtocol.parseIncomingMessages(response);
				const [containedAForm, containedAnAttachment] = this.handleRetrieveMessage(messages);

				const analyticQuirk_receivedMessageFromPeer = messages.some((m) => !m.isSystemMessage && !m.sender.isMe);
				if (analyticQuirk_receivedMessageFromPeer) {
					// TODO: DEV-14218 make category generic/fetch from AnalyticContext
					this.analyticService.trackEvent(this.communicatorProtocol.analyticCategory, 'Received message from peer');

					if (containedAForm) {
						this.analyticService.trackEvent(this.communicatorProtocol.analyticCategory, 'Received form from peer');
					}

					if (containedAnAttachment) {
						this.analyticService.trackEvent(this.communicatorProtocol.analyticCategory, 'Received attachment from peer');
					}
				}
			}),

			//this is topic case_closed_v2
			this.controller.on(
				this.communicatorProtocol.topics.caseClosed,
				async (closedCase: { caseId: number; closedByParticipantId: number; isAnonymized: boolean }) => {
					if (closedCase.caseId.toString() === this.connectionId) {
						this.presentation.write({
							isCompleted: true,
						});

						const ownParticipantId = this.presentation.read().messages.find((m) => m.type === 'chatMessage' && m.isMe)?.fromId;

						if (closedCase.isAnonymized) {
							// Only show the dialog if we did not trigger the case closure ourselves.
							if (closedCase.closedByParticipantId !== ownParticipantId) {
								await this.dialogAlertService.show({
									header: I18nAndL10n.translate('Messaging.CaseAnonymized.Dialog.Title'),
									description: I18nAndL10n.translate('Messaging.CaseAnonymized.Dialog.Text'),
									abortText: I18nAndL10n.translate('Messaging.CaseAnonymized.Dialog.Ok.Button'),
								});
							}

							this.routerService.navigateByUrl('/visits');
							this.caseCommunicator.removeMessagingCase(closedCase.caseId);
						}
					}

					// Always remove the messaging case if it has been anonymized.
					if (closedCase.isAnonymized) {
						this.caseCommunicator.removeMessagingCase(closedCase.caseId);
					}
				},
			),

			this.controller.on(this.communicatorProtocol.topics.peerHasReadUntil, (payload: PeerHasBeenReadUntilPayload) => {
				// this even is triggered for all cases so we need to filter out for the current case
				if (payload.caseId.toString() === this.connectionId) {
					const messages = this.presentation.read().messages;

					const messageIndex = this.getMessageWithReceiptIndex(messages, payload.data);

					const updatedMessages = messages.map((message, index) => {
						if (message.type === 'chatMessage' && index === messageIndex) {
							return {
								...message,
								peerReadTimestamp: payload.data.timestamp,
								peerReadFullName: payload.data.readBy.commonName,
							};
						}

						return message;
					});

					this.presentation.write({ messages: updatedMessages });
				}
			}),

			this.controller.on('app_error', (error) => {
				this.logger.logError('App error', error);
				this.metaData.onConnectionError?.(error);
			}),
			this.controller.on('duplicate_connection', () => {
				this.logger.logInformation('Duplicate connection detected, another connection was opened for this chat. Ending session.');
				this.metaData.onConnectionError?.('duplicate_connection');
			}),
		];
	}

	private handleRetrieveMessage(messages: ParsedMessage[], isOld = false): [containedAForm: boolean, containedAnAttachment: boolean] {
		// We need a way to tell how many have written in the chat to determine if
		// we should show the "unread messages" banner.
		const actors = new Set<number>();
		const hasReadUntilTimestamp = this.metaData.hasReadUntilTimestamp;

		const controlledMessage = (message: ParsedMessage): ControlledChatMessage | ControlledSystemMessage => {
			let text = message.text;
			if (text != null) {
				text = text.trim();
			}

			if (message.isSystemMessage && text) {
				actors.add(0);

				return {
					type: 'systemMessage',
					text,
					id: v4(),
					fromId: v4(),
					timestamp: this.dateService.create(message.timestamp),
					hasBeenRead: !hasReadUntilTimestamp?.isBefore(message.timestamp),
				};
			}

			let senderNameWithTitle: string;
			let fromId: number;
			let senderName: string;
			let senderImageUrl: string | null;
			let isMe: boolean;
			let isAutoreplyMessage: boolean;
			let fromInitials: string | null = null;

			if (message.autoReplyReception != null) {
				senderNameWithTitle = message.autoReplyReception.name ?? '';
				fromId = message.autoReplyReception.receptionId;
				senderName = message.autoReplyReception.name || '';
				senderImageUrl = message.autoReplyReception.profileImageUrl;
				isMe = false;
				isAutoreplyMessage = true;
			} else {
				senderNameWithTitle = `${message.sender.title ? message.sender.title + ', ' : ''}${message.sender.name}`;
				fromId = message.sender.id;
				senderName = message.sender.name;
				senderImageUrl = message.sender.profileImageUrl;
				isMe = message.sender.isMe;
				isAutoreplyMessage = false;
				fromInitials = message.sender.fromInitials;
			}

			actors.add(fromId);

			return {
				type: 'chatMessage',
				id: v4(),
				hasRendered: isOld,
				text,
				senderName,
				fromInitials,
				senderNameWithTitle,
				senderImageUrl,
				fromId,
				isMe,
				isAutoreplyMessage,
				timestamp: this.dateService.create(message.timestamp),
				hasBeenRead: !hasReadUntilTimestamp?.isBefore(message.timestamp),
				isRedRobinInitialMessage: message.redRobinSessionId != null,
				staleFileWorkers: message.files.map(
					(file) =>
						new FileWorkerStale({
							downloadUrl: file.url,
							fileName: file.fileName,
							fileSize: file.length,
							fileType: file.type,
							thumbnailUrl: file.url + '/thumbnail',
						}),
				),
				// We need to register the forms that may be contained within a message.
				forms: this.formManager.addRespondants(
					message.responseStatuses.map((respondant) => ({
						...respondant,
						issueTrackingId: MessagingCaseModel.MESSAGE_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + this.connectionId,
						dataProvider: {
							type: 'respondant',
							respondantId: respondant.respondantId,
						},
					})),
				),
			};
		};

		let analyticQuirk_didContainAForm = false;
		let analyticQuirk_didContainAnAttachment = false;

		let insertNewUnreadIndex = -1;
		const parsedMessages: ControlledMessage[] = [];
		for (let index = 0; index < messages.length; index++) {
			const rawMessage = messages[index];
			const message = controlledMessage(rawMessage);

			if (!analyticQuirk_didContainAForm) {
				analyticQuirk_didContainAForm = rawMessage.responseStatuses.length > 0;
			}

			if (!analyticQuirk_didContainAnAttachment) {
				analyticQuirk_didContainAnAttachment = rawMessage.files.length > 0;
			}

			parsedMessages.push(message);

			// We already have a new unread message, we don't want the last message to
			// be flagged as the unread one.
			if (insertNewUnreadIndex !== -1) continue;

			if (hasReadUntilTimestamp?.isBefore(message.timestamp)) {
				insertNewUnreadIndex = index;
			}
		}

		// We don't want to show unread if there's only one that have been active in
		// the chat.
		if (actors.size > 1 && insertNewUnreadIndex > 0) {
			parsedMessages.splice(insertNewUnreadIndex, 0, {
				type: 'unreadTimestampMessage',
				id: v4(),
				fromId: v4(),
			});
		}

		if (isOld) {
			this.resetMessages();
		}

		this.updateMessages(parsedMessages);

		return [analyticQuirk_didContainAForm, analyticQuirk_didContainAnAttachment];
	}

	private updateMessages(messages: ControlledMessage[]): void {
		this.presentation.write((prev) => {
			const prevMessages = prev.extract().messages as unknown as ControlledMessage[];

			return {
				messages: [...prevMessages, ...messages],
			};
		});
	}

	// biome-ignore lint/correctness/noUnusedPrivateClassMembers: suppressed due to migration - should be fixed
	private getUserMessageById(id: string): ControlledChatMessage | ControlledSystemMessage {
		const { messages } = this.presentation.read();
		const message = messages.find((message) => message.id === id);

		if (message == null) throw new Error(`Could not find a message with id ${id}`);
		if (message.type !== 'chatMessage' && message.type !== 'systemMessage')
			throw new Error(`The message was found but it was not of type "ControlledChatMessage" but rather: ${message.type}`);

		return message;
	}

	private resetMessages(): void {
		this.presentation.write({
			messages: [],
		});
	}
}
